import React, { forwardRef, useMemo, type ReactNode } from 'react';
import { StyleSheet, type TextInput as RNTextInput } from 'react-native';
import type { TextInputProps as PaperTextInputProps } from 'react-native-paper';
import { TextInput } from './TextInput';

type Props = {
  label: string;
  showLabel?: boolean;
  value: string;
  onChange?: (value: string) => void;
  icon?: ReactNode;
  onIconPress?: () => void;
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  allowNegative?: boolean;
  error?: boolean;
  validateOnChange?: boolean;
} & Omit<
  PaperTextInputProps,
  'onChangeText' | 'onChange' | 'value' | 'keyboardType'
>;

export const NumberInput = forwardRef<RNTextInput, Props>(
  (
    {
      label,
      showLabel = true,
      value,
      onChange,
      icon,
      style,
      disabled,
      onIconPress,
      min,
      max,
      allowDecimals = true,
      allowNegative = true,
      error,
      validateOnChange = false,
      ...otherProps
    }: Props,
    ref,
  ) => {
    const handleChange = (text: string) => {
      if (!onChange) return;

      // Allow empty string
      if (text === '') {
        onChange('');
        return;
      }

      // Allow minus sign at the start if negative numbers are allowed
      if (allowNegative && text === '-') {
        onChange(text);
        return;
      }

      // Build regex pattern based on options
      let pattern: RegExp;
      if (allowDecimals) {
        // Allow decimals: optional minus, digits, optional decimal point and digits
        pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
      } else {
        // Only integers: optional minus and digits
        pattern = allowNegative ? /^-?\d*$/ : /^\d*$/;
      }

      // Test if the input matches the pattern
      if (!pattern.test(text)) {
        return;
      }

      onChange(text);
    };

    // Validation for min/max bounds (visual feedback only)
    const isWithinBounds = (numStr: string): boolean => {
      if (!numStr || numStr === '-') return true;
      const numValue = parseFloat(numStr);
      if (isNaN(numValue)) return true;
      if (min !== undefined && numValue < min) return false;
      if (max !== undefined && numValue > max) return false;
      return true;
    };

    // Show error if:
    // 1. error prop is explicitly set, OR
    // 2. validateOnChange is true AND value is outside bounds
    const hasError =
      error || (validateOnChange && value.length > 0 && !isWithinBounds(value));

    // Memoize error style to avoid recreation
    const errorStyle = useMemo(
      () => (hasError ? styles.errorInput : undefined),
      [hasError],
    );

    return (
      <TextInput
        ref={ref}
        label={label}
        showLabel={showLabel}
        value={value}
        onChange={handleChange}
        icon={icon}
        onIconPress={onIconPress}
        disabled={disabled}
        keyboardType="decimal-pad"
        style={[errorStyle, style]}
        {...otherProps}
      />
    );
  },
);
NumberInput.displayName = 'NumberInput';

const styles = StyleSheet.create({
  errorInput: {
    // borderColor: Colors.pink0,
    // borderWidth: 1,
  },
});
